import { isValidElement, type JSX, type ReactNode } from 'react';
import { Outlet, useOutlet, useOutletContext } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { USER_ROLES, type UserRole } from '@/lib/auth';

const defaultFallback = (
  <section
    className="container flex flex-col items-center justify-center gap-md py-12 text-center"
    aria-live="polite"
  >
    <div className="w-full max-w-xl rounded-2xl border border-brand-primary/20 bg-white/70 p-8 shadow-card">
      <h2 className="text-2xl font-semibold text-brand-primary">Access restricted</h2>
      <p className="mt-sm text-text-primary/80">
        You don&rsquo;t have the required permissions to open this area. Reach out to an
        administrator if you believe this is a mistake.
      </p>
    </div>
  </section>
);

interface RoleGateProps {
  allowed: readonly UserRole[];
  fallback?: ReactNode;
  children: ReactNode;
}

export function RoleGate({
  allowed,
  fallback = defaultFallback,
  children,
}: RoleGateProps): JSX.Element {
  const { status, hasRole } = useAuth();
  const outlet = useOutlet();
  const outletContext = useOutletContext();

  if (status === 'loading') {
    return (
      <section className="container flex flex-col items-center justify-center gap-md py-12 text-center">
        <div className="h-16 w-16 animate-spin rounded-full border-4 border-brand-primary border-t-transparent" />
        <p className="text-text-primary/70">Preparing your workspace&hellip;</p>
      </section>
    );
  }

  if (hasRole(...allowed)) {
    const content = children ?? outlet;

    if (isValidElement(content)) {
      if (content.type === Outlet) {
        return <Outlet context={outletContext} />;
      }

      return <>{content}</>;
    }

    return <>{content}</>;
  }

  return <>{fallback}</>;
}

export const ADMIN_ONLY: readonly UserRole[] = [USER_ROLES.administrator];
export const TENANT_ONLY: readonly UserRole[] = [USER_ROLES.tenant];
export const MANAGER_ONLY: readonly UserRole[] = [USER_ROLES.manager];
